/**
 * TextDiff Component
 * 
 * Side-by-Side oder Inline Diff für Text-Inhalte.
 * Verwendet react-diff-viewer-continued.
 */

import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface TextDiffProps {
  oldValue: string
  newValue: string
  oldLabel?: string
  newLabel?: string
  viewMode?: 'split' | 'unified'
}

// Custom styles for dark theme
const diffStyles = {
  variables: {
    dark: {
      diffViewerBackground: '#1a1a1a',
      diffViewerColor: '#e0e0e0',
      addedBackground: 'rgba(40, 167, 69, 0.15)',
      addedColor: '#e0e0e0',
      removedBackground: 'rgba(220, 53, 69, 0.15)',
      removedColor: '#e0e0e0',
      wordAddedBackground: 'rgba(40, 167, 69, 0.4)',
      wordRemovedBackground: 'rgba(220, 53, 69, 0.4)',
      addedGutterBackground: 'rgba(40, 167, 69, 0.2)',
      removedGutterBackground: 'rgba(220, 53, 69, 0.2)',
      gutterBackground: '#1a1a1a',
      gutterBackgroundDark: '#1a1a1a',
      highlightBackground: 'rgba(255, 193, 7, 0.2)',
      highlightGutterBackground: 'rgba(255, 193, 7, 0.2)',
      codeFoldGutterBackground: '#2a2a2a',
      codeFoldBackground: '#2a2a2a',
      emptyLineBackground: '#1a1a1a',
      gutterColor: '#6c757d',
      addedGutterColor: '#28a745',
      removedGutterColor: '#dc3545',
      codeFoldContentColor: '#6c757d',
      diffViewerTitleBackground: '#2a2a2a',
      diffViewerTitleColor: '#e0e0e0',
      diffViewerTitleBorderColor: '#3a3a3a',
    },
  },
  line: {
    padding: '4px 8px',
    fontSize: '13px',
    fontFamily: 'monospace', // design-allow: react-diff-viewer styles API, not JSX inline style
  },
  contentText: {
    fontFamily: 'inherit', // design-allow: react-diff-viewer styles API, not JSX inline style
    fontSize: 'inherit',
  },
  gutter: {
    padding: '4px 8px',
    minWidth: '40px',
  },
  marker: {
    padding: '4px',
  },
  titleBlock: {
    padding: '8px 12px',
    borderBottom: '1px solid var(--goki-border)',
  },
}

export function TextDiff({
  oldValue,
  newValue,
  oldLabel = 'Alt',
  newLabel = 'Neu',
  viewMode = 'split',
}: TextDiffProps) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'split' | 'unified'>(viewMode)

  // Check if there are actual changes
  const hasChanges = oldValue !== newValue

  if (!hasChanges) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2" />
        {t('content.textDiff')}
      </div>
    )
  }

  return (
    <div className="text-diff-container">
      {/* View Mode Toggle */}
      <div className="d-flex justify-content-end mb-2">
        <div className="btn-group btn-group-sm">
          <button
            type="button"
            className={`btn ${mode === 'split' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setMode('split')}
          >
            <i className="bi bi-layout-split me-1" />
            Split
          </button>
          <button
            type="button"
            className={`btn ${mode === 'unified' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setMode('unified')}
          >
            <i className="bi bi-layout-text-window me-1" />
            Inline
          </button>
        </div>
      </div>

      {/* Diff Viewer */}
      <div className="diff-viewer-wrapper border rounded overflow-hidden">
        <ReactDiffViewer
          oldValue={oldValue}
          newValue={newValue}
          splitView={mode === 'split'}
          useDarkTheme={true}
          compareMethod={DiffMethod.WORDS}
          styles={diffStyles}
          leftTitle={oldLabel}
          rightTitle={newLabel}
          showDiffOnly={false}
          extraLinesSurroundingDiff={3}
        />
      </div>
    </div>
  )
}


